import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  MdChevronLeft, MdMic, MdVolumeUp, MdVolumeOff,
  MdCameraAlt, MdSearch, MdFolderOpen, MdMemory, MdSettings,
  MdCheckCircle, MdSmartToy, MdFiberManualRecord, MdStopCircle,
} from 'react-icons/md';
import { useProfile } from '../contexts/ProfileContext';
import { useApp } from '../contexts/AppContext';
import { sendChatMessage } from '../services/backendAPI';
import { useTTS } from '../hooks/useTTS';
import { useVoiceInput } from '../hooks/useVoiceInput';
import { useSaveSession } from '../hooks/useSaveSession';
import ChartWrapper from '../components/ChartWrapper';
import txData from '../data/transactions.json';
import goalsData from '../data/goals.json';
import { generateId } from '../utils/uuid';
import ahmedSmall from '../personas/ahmed_banking_profile_small.json';
import sarahChen from '../personas/sarah_chen_banking_profile_small.json';
import jasonJones from '../personas/jason_jones_banking_profile_small.json';
import samOkafor from '../personas/sam_okafor_profile_small.json';

const transactionsData = txData.transactionsData;
const accountTransactions = txData.accountTransactions;

async function callBackendAPI(userMessage, conversationHistory, provider = 'cohere', model = null, profileData = null, signal = null, noThink = false) {
  return sendChatMessage({ message: userMessage, history: conversationHistory || [], provider, model, profileData, signal, noThink });
}

const AGENT_STEPS = [
  { Icon: MdSearch,      label: 'Parsing intent' },
  { Icon: MdFolderOpen,  label: 'Loading account context' },
  { Icon: MdMemory,      label: 'Analysing request' },
  { Icon: MdSettings,    label: 'Executing workflow' },
  { Icon: MdCheckCircle, label: 'Action complete' },
];

const AgentFlowCard = ({ command }) => {
  const [step, setStep] = useState(0);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const delays = [600, 1400, 2300, 3200, 4000];
    const timers = delays.map((ms, i) =>
      setTimeout(() => {
        setStep(i + 1);
        if (i === AGENT_STEPS.length - 1) setDone(true);
      }, ms)
    );
    return () => timers.forEach(clearTimeout);
  }, []);

  return (
    <div className="agent-card">
      <div className="agent-card-header">
        <span className={`agent-robot-icon${done ? '' : ' agent-robot-pulse'}`}><MdSmartToy size={20} /></span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="agent-card-title">Hey Google — Agent</div>
          <div className="agent-card-command">"{command}"</div>
        </div>
        <span className={`agent-badge ${done ? 'agent-badge-done' : 'agent-badge-running'}`}>
          {done ? '✓ Done' : 'Running…'}
        </span>
      </div>

      <div className="agent-steps">
        {AGENT_STEPS.slice(0, step).map((s, i) => {
          const isCurrent = i === step - 1 && !done;
          return (
            <div key={i} className="agent-step">
              <span className="agent-step-status">
                {isCurrent ? <span className="agent-step-spinner" /> : <MdCheckCircle size={11} />}
              </span>
              <span className="agent-step-emoji"><s.Icon size={14} /></span>
              <span style={{ color: isCurrent ? 'var(--text-dark)' : 'var(--text-medium)' }}>{s.label}</span>
            </div>
          );
        })}
      </div>

      {done && (
        <div className="agent-stub-footer">
          ⚡ Stub — agentic workflow would execute here
        </div>
      )}
    </div>
  );
};

const VoiceBotScreen = ({ onNavigate, onLog }) => {
  const { loadedProfileData, setLoadedProfileData: onSetLoadedProfileData } = useProfile();
  const { selectedBackend, selectedModel, ttsEngine, setTtsEngine, elVoiceId, setElVoiceId, openaiVoiceId, setOpenaiVoiceId, qwenVoiceId, setQwenVoiceId, voiceOut, setVoiceOut, ollamaNoThink } = useApp();

  const [isProcessing,    setIsProcessing]    = useState(false);
  const [messages,        setMessages]        = useState([]);
  const [selectedProfile, setSelectedProfile] = useState('default');
  const [profileLoading,  setProfileLoading]  = useState(false);
  const [agentCommand,    setAgentCommand]    = useState(null);

  const historyRef            = useRef([]);
  const historyEndRef         = useRef(null);
  const vbScrollRef           = useRef(null);
  const handleVoiceInputRef   = useRef(null);
  const abortControllerRef    = useRef(null);
  const selectedBackendRef    = useRef(selectedBackend);
  const selectedModelRef      = useRef(selectedModel);
  const chatProfilePayloadRef = useRef(null);
  const speakRef              = useRef(null);

  selectedBackendRef.current = selectedBackend;
  selectedModelRef.current   = selectedModel;

  const log = (type, msg) => onLog?.(type, msg);

  const {
    isSpeaking, availableVoices, selectedVoiceName, setSelectedVoiceName,
    currentAudioRef, speak, cancelSpeech,
  } = useTTS({ selectedBackend, voiceOut, ttsEngine, setTtsEngine, elVoiceId, setElVoiceId, openaiVoiceId, setOpenaiVoiceId, qwenVoiceId, setQwenVoiceId, log });
  speakRef.current = speak;

  const { saveStatus, handleSaveSession } = useSaveSession({
    messages, loadedProfileData, selectedBackend, selectedModel, scrollRef: vbScrollRef, log,
  });

  useEffect(() => { historyRef.current = messages; }, [messages]);
  useEffect(() => { historyEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);
  useEffect(() => { historyEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [isProcessing]);

  const chatProfilePayload = useMemo(() => {
    if (!loadedProfileData) return null;
    if (loadedProfileData.source === 'default') return { source: 'default', transactionsData, accountTransactions };
    return { source: loadedProfileData.source, profile: loadedProfileData.profile };
  }, [loadedProfileData]);
  chatProfilePayloadRef.current = chatProfilePayload;

  const loadProfile = async () => {
    setProfileLoading(true);
    log('system', `Loading profile: ${selectedProfile}`);
    let payloadObj = null;
    let profileDataToStore = null;
    if (selectedProfile === 'default') {
      payloadObj = { source: 'default', transactionsData, accountTransactions };
      profileDataToStore = { name: 'Default', goals: goalsData.goalsData, profile: null, source: 'default' };
    } else if (selectedProfile === 'ahmed-small') {
      payloadObj = { source: 'ahmed-small', profile: ahmedSmall };
      profileDataToStore = { name: 'Ahmed Al-Rashidi', goals: goalsData.goalsData, profile: ahmedSmall, source: 'ahmed-small' };
    } else if (selectedProfile === 'sarah-chen') {
      payloadObj = { source: 'sarah-chen', profile: sarahChen };
      profileDataToStore = { name: 'Sarah Chen', goals: sarahChen.goals || goalsData.goalsData, profile: sarahChen, source: 'sarah-chen' };
    } else if (selectedProfile === 'jason-jones') {
      payloadObj = { source: 'jason-jones', profile: jasonJones };
      profileDataToStore = { name: 'Jason Jones', goals: jasonJones.goals || goalsData.goalsData, profile: jasonJones, source: 'jason-jones' };
    } else if (selectedProfile === 'sam-okafor') {
      payloadObj = { source: 'sam-okafor', profile: samOkafor };
      profileDataToStore = { name: 'Sam Okafor', goals: samOkafor.goals || goalsData.goalsData, profile: samOkafor, source: 'sam-okafor' };
    }
    if (!payloadObj) { setProfileLoading(false); return; }
    if (onSetLoadedProfileData) onSetLoadedProfileData(profileDataToStore);
    const backend = selectedBackendRef.current;
    const model   = selectedModelRef.current;
    const profilePayload = { source: payloadObj.source, profile: payloadObj.profile, transactionsData: payloadObj.transactionsData, accountTransactions: payloadObj.accountTransactions };
    try {
      await callBackendAPI(`PROFILE_UPLOAD:${JSON.stringify(payloadObj)}`, [], backend, model, profilePayload);
      log('system', `Profile ready: ${profileDataToStore.name}`);
    } catch (e) {
      log('error', `Profile upload failed: ${e.message}`);
    } finally {
      setProfileLoading(false);
    }
  };

  const BANKBOT_TRIGGER = /^(?:hey|ok)\s+iva[,:]?\s*/i;

  const runAgentFlow = (command) => {
    log('system', `🤖 Agent triggered: "${command}"`);
    const userMsg  = { id: generateId(), text: `Hey IVA, ${command}`, isUser: true };
    const agentMsg = { id: generateId(), isAgent: true, command };
    setMessages(prev => [...prev, userMsg, agentMsg]);
    setIsProcessing(true);
    setAgentCommand(command);
    setTimeout(() => {
      setIsProcessing(false);
      setAgentCommand(null);
      const reply = `🤖 I took the following action:\n\n**${command}**\n\nThe agentic workflow was initiated and all steps completed successfully. *(This is a stub — real execution will be wired up here.)*`;
      setMessages(prev => [...prev, { id: generateId(), text: reply, isUser: false }]);
      speakRef.current?.(`I took the following action: ${command}.`);
      log('system', `🤖 Agent complete: "${command}"`);
      log('status', 'Ready');
    }, 4400);
  };

  const handleVoiceInput = async (text) => {
    if (!text.trim()) { log('status', 'Empty transcript — ignored'); return; }
    setTranscript('');

    const agentMatch = text.trim().match(BANKBOT_TRIGGER);
    if (agentMatch) {
      const command = text.trim().slice(agentMatch[0].length).trim() || 'help me';
      runAgentFlow(command);
      return;
    }

    setIsProcessing(true);
    const userMsg = { id: generateId(), text: text.trim(), isUser: true };
    setMessages(prev => [...prev, userMsg]);

    const backend = selectedBackendRef.current;
    const model   = selectedModelRef.current;
    const profile = chatProfilePayloadRef.current;
    log('api', `Backend: ${backend.toUpperCase()}`);
    log('api', `Prompt: "${text.trim().slice(0, 80)}${text.length > 80 ? '…' : ''}"`);

    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      const history = historyRef.current.slice(-10).map(m => ({ role: m.isUser ? 'user' : 'assistant', content: m.text }));
      const { text: reply, model: usedModel } = await callBackendAPI(text.trim(), history, backend, model, profile, controller.signal, ollamaNoThink);
      if (usedModel) log('api', `Model: ${usedModel}`);
      log('bot', `Response (${reply.length} chars): "${reply.slice(0, 100)}${reply.length > 100 ? '…' : ''}"`);
      const botMsg = { id: generateId(), text: reply, isUser: false };
      setMessages(prev => [...prev, botMsg]);
      speak(reply);
    } catch (e) {
      if (e.name !== 'AbortError' && e.message !== 'AbortError') {
        log('error', `API call failed: ${e.message}`);
        const errMsg = { id: generateId(), text: "Sorry, I couldn't reach the server. Please try again.", isUser: false };
        setMessages(prev => [...prev, errMsg]);
      } else {
        log('status', 'Stopped');
      }
    } finally {
      abortControllerRef.current = null;
      setIsProcessing(false);
      log('status', 'Ready');
    }
  };
  handleVoiceInputRef.current = handleVoiceInput;

  const { isListening, transcript, setTranscript, supported, toggleListening } = useVoiceInput({
    onFinalTranscript: (text) => handleVoiceInputRef.current?.(text),
    isProcessing,
    isSpeaking,
    cancelSpeech,
    log,
  });

  const clearConversation = () => {
    window.speechSynthesis?.cancel();
    setMessages([]);
    setTranscript('');
    log('system', '--- Conversation cleared ---');
  };

  const micState = agentCommand ? 'agent' : isProcessing ? 'thinking' : isSpeaking ? 'speaking' : isListening ? 'listening' : 'idle';
  const statusText = {
    agent:     `Running: "${agentCommand}"`,
    idle:      messages.length === 0 ? 'Tap the mic to start' : 'Tap to speak again',
    listening: 'Listening…',
    thinking:  'Thinking…',
    speaking:  'Speaking…',
  }[micState];

  if (!supported) {
    return (
      <div className="screen vb-screen">
        <div className="simple-header">
          <MdChevronLeft className="back-icon" onClick={() => onNavigate('home')} />
          <h2 className="header-title">Voice Assistant</h2>
          <div style={{ width: 28 }} />
        </div>
        <div className="vb-no-support">
          <div style={{ marginBottom: 12, color: 'var(--text-light)' }}><MdMic size={40} /></div>
          <strong>Voice not supported</strong>
          <p>Your browser doesn't support the Web Speech API.<br />Try Chrome or Edge for full voice support.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="screen vb-screen">
      <div className="vb-header">
        <MdChevronLeft style={{ fontSize: 22, color: '#fff', cursor: 'pointer', flexShrink: 0 }} onClick={() => { window.speechSynthesis?.cancel(); onNavigate('home'); }} />
        <span className="vb-header-title">Voice Assistant</span>
        <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
          {ttsEngine === 'browser' && availableVoices.length > 0 && (
            <select value={selectedVoiceName} onChange={e => { setSelectedVoiceName(e.target.value); log('tts', `Voice: ${e.target.value}`); }}
              style={{ fontSize: 9, padding: '2px 3px', borderRadius: 4, border: '1px solid rgba(255,255,255,0.25)', background: 'rgba(255,255,255,0.12)', color: '#fff', cursor: 'pointer', maxWidth: 100 }}>
              {availableVoices.map(v => <option key={v.name} value={v.name} style={{ color: '#000' }}>{v.name.replace(/ \(.*\)/, '')}</option>)}
            </select>
          )}
          <button className={`vb-header-action${voiceOut ? ' active' : ''}`}
            onClick={() => { const next = !voiceOut; setVoiceOut(next); window.speechSynthesis?.cancel(); if (currentAudioRef.current) { currentAudioRef.current.pause(); currentAudioRef.current = null; } log('tts', `Voice output ${next ? 'enabled' : 'muted'}`); }}
            title="Toggle voice output">
            {voiceOut ? <MdVolumeUp size={16} /> : <MdVolumeOff size={16} />}
          </button>
          <button
            className="vb-header-action save-session-btn"
            onClick={handleSaveSession}
            title="Save session log + screenshot"
            disabled={saveStatus === 'saving'}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: '4px 6px',
              backgroundColor: saveStatus === 'saved'  ? 'rgba(0,220,100,0.35)'
                             : saveStatus === 'error'  ? 'rgba(255,80,80,0.35)'
                             : saveStatus === 'saving' ? 'rgba(255,255,255,0.1)'
                             : 'rgba(255,255,255,0.12)',
              border: '1px solid rgba(255,255,255,0.25)',
              borderRadius: 5,
              cursor: saveStatus === 'saving' ? 'default' : 'pointer',
              transition: 'background-color 0.3s',
            }}
          >
            <MdCameraAlt style={{ fontSize: 15, color: '#fff', opacity: saveStatus === 'saving' ? 0.4 : 1 }} />
          </button>
          {messages.length > 0 && (
            <button className="vb-header-action" onClick={clearConversation} title="Clear conversation">Clear</button>
          )}
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', background: 'var(--bg-card)', borderBottom: '1px solid var(--border-color)', flexShrink: 0 }}>
        {loadedProfileData ? (
          <>
            <span style={{ fontSize: 11, color: 'var(--text-medium)', flexShrink: 0 }}>Profile:</span>
            <span style={{ fontSize: 11, fontWeight: 'bold', color: 'var(--td-green)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              ✓ {loadedProfileData.name}
            </span>
            <select value={selectedProfile} onChange={e => setSelectedProfile(e.target.value)}
              style={{ fontSize: 10, padding: '2px 4px', borderRadius: 4, border: '1px solid var(--border-color)', background: 'var(--bg-screen)', color: 'var(--text-dark)', cursor: 'pointer' }}>
              <option value="default">Default</option>
              <option value="ahmed-small">Ahmed Al-Rashidi</option>
              <option value="sarah-chen">Sarah Chen</option>
              <option value="jason-jones">Jason Jones</option>
              <option value="sam-okafor">Sam Okafor</option>
            </select>
            <button onClick={loadProfile} disabled={profileLoading}
              style={{ fontSize: 10, padding: '3px 8px', borderRadius: 4, border: 'none', background: 'var(--td-green)', color: '#fff', cursor: profileLoading ? 'not-allowed' : 'pointer', opacity: profileLoading ? 0.6 : 1, flexShrink: 0 }}>
              {profileLoading ? '…' : 'Switch'}
            </button>
          </>
        ) : (
          <>
            <span style={{ fontSize: 11, color: 'var(--text-medium)', flexShrink: 0 }}>Load profile:</span>
            <select value={selectedProfile} onChange={e => setSelectedProfile(e.target.value)}
              style={{ flex: 1, fontSize: 10, padding: '2px 4px', borderRadius: 4, border: '1px solid var(--border-color)', background: 'var(--bg-screen)', color: 'var(--text-dark)', cursor: 'pointer' }}>
              <option value="default">Default</option>
              <option value="ahmed-small">Ahmed Al-Rashidi</option>
              <option value="sarah-chen">Sarah Chen</option>
              <option value="jason-jones">Jason Jones</option>
              <option value="sam-okafor">Sam Okafor</option>
            </select>
            <button onClick={loadProfile} disabled={profileLoading}
              style={{ fontSize: 10, padding: '3px 8px', borderRadius: 4, border: 'none', background: 'var(--td-green)', color: '#fff', cursor: profileLoading ? 'not-allowed' : 'pointer', opacity: profileLoading ? 0.6 : 1, flexShrink: 0 }}>
              {profileLoading ? '…' : 'Load'}
            </button>
          </>
        )}
      </div>

      <div className="vb-history" ref={vbScrollRef}>
        {messages.length === 0 && (
          <div style={{ textAlign: 'center', marginTop: 24, color: 'var(--text-medium)', fontSize: 13, lineHeight: 1.7 }}>
            <div style={{ marginBottom: 8, color: 'var(--text-light)' }}><MdMic size={36} /></div>
            {loadedProfileData
              ? <>Hi <strong style={{ color: 'var(--text-dark)' }}>{loadedProfileData.name}</strong>! Ask me anything about your finances.</>
              : <>Ask me anything about your finances, accounts, or spending.</>}
          </div>
        )}
        {messages.map(m => {
          if (m.isAgent) return <AgentFlowCard key={m.id} command={m.command} />;
          return (
            <div key={m.id} className={`vb-bubble ${m.isUser ? 'user' : 'bot'}${!m.isUser ? ' vb-bubble-wide' : ''}`}>
              <div className="vb-bubble-label">{m.isUser ? 'You' : 'Assistant'}</div>
              {m.isUser ? m.text : <ChartWrapper text={m.text} />}
            </div>
          );
        })}
        {isListening && transcript && (
          <div className="vb-bubble user" style={{ opacity: 0.6, fontStyle: 'italic' }}>
            <div className="vb-bubble-label">You</div>
            {transcript}…
          </div>
        )}
        {isProcessing && (
          <div className="vb-bubble bot vb-bubble-wide">
            <div className="vb-bubble-label">Assistant</div>
            <div className="vb-thinking-row">
              <div className="vb-dot"/><div className="vb-dot"/><div className="vb-dot"/>
            </div>
          </div>
        )}
        <div ref={historyEndRef} />
      </div>

      <div className="vb-orb-area">
        <div className={`vb-status ${micState !== 'idle' ? micState : ''}`}>{statusText}</div>

        <div className="vb-mic-wrap">
          {isListening && (<><div className="vb-ring" /><div className="vb-ring" /><div className="vb-ring" /></>)}
          {isProcessing ? (
            <button className="vb-mic-btn vb-mic-stop" onClick={() => { abortControllerRef.current?.abort(); cancelSpeech(); }} title="Stop">
              <MdStopCircle size={30} />
            </button>
          ) : (
            <button className={`vb-mic-btn ${micState === 'agent' ? 'thinking' : micState}`} onClick={toggleListening} disabled={!!agentCommand}>
              {micState === 'agent'   ? <MdSmartToy size={30} style={{ animation: 'agent-pulse 1s ease-in-out infinite' }} />
               : micState === 'speaking' ? <div className="vb-wave">{[0,1,2,3,4].map(i => <div key={i} className="vb-bar" style={{ animationDelay: `${i * 0.1}s` }} />)}</div>
               : <MdMic size={isListening ? 26 : 30} />}
            </button>
          )}
        </div>

        {transcript && <div className="vb-transcript">"{transcript}"</div>}
        {!transcript && <div className="vb-hint">{isListening ? <><MdFiberManualRecord size={10} style={{ color: '#e53935', verticalAlign: 'middle', marginRight: 4 }} />Recording — tap to stop</> : isSpeaking ? 'Tap mic to interrupt' : micState === 'idle' ? 'Tap to start recording' : ''}</div>}
      </div>
    </div>
  );
};

export default VoiceBotScreen;
